import React from 'react';
import { AnimalKind, AnimalType, Gender, IStemAnimal } from 'shared/api/api';
import ForwardArrowGrey from 'shared/assets/src-assets/png/forward_arrow_bottom_grey.png';
import { localized } from 'shared/state/i18n/i18n';
import SkioldStableSectionPicker from 'web/view/components/location/location-picker/skiold-stable-section-picker';
import { SkioldDatePicker } from 'web/view/components/skiold-components/skiold-date-picker/skiold-date-picker';
import { SkioldFormInput } from 'web/view/components/skiold-components/skiold-input/skiold-form-input';
import { SkioldFormIntegerInput } from 'web/view/components/skiold-components/skiold-integer-input/skiold-form-integer-input';
import { SkioldTouchableOpacity } from 'web/view/components/skiold-components/skiold-touchable-opacity';
import { SkioldImage } from 'web/view/components/utils/svg/skiold-image';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { TextWeb } from 'web/web-helpers/styled-text-components';
import { GenderPicker } from '../../gender-picker/gender-picker';
import SkioldFormPenPicker from 'web/view/components/location/location-picker/skiold-form-pen-picker';
import { FormRow } from 'web/view/components/skiold-components/skiold-forms-wrapper/skiold-forms-wrapper-types';
import SkioldFeedCurvePicker from 'web/view/components/process-equipment/skiold-feed-curve-picker';
import { SkioldFormTextField } from 'web/view/components/skiold-components/skiold-forms-wrapper/skiold-form-text';
import { getLocationString } from 'shared/helpers/location-helper';
import { GetAgeInDays } from '../../../../../../shared/helpers/growth-pigs-helper/growth-pig-weight-curve-helper';
import { calculateAgeInWeeks } from 'shared/helpers/general-helpers';

export function getGenderRow(gender: Gender, genderChanged: (newGender: Gender) => void): FormRow {
	const picker = <GenderPicker gender={gender} onGenderChanged={genderChanged} />;

	const row: FormRow = {
		name: localized('Gender'),
		component: picker,
	};

	return row;
}

export function getDateRow(
	selectedDate: Date,
	minDate: Date | undefined,
	onDateChanged: (date: Date) => void,
): FormRow {
	return {
		name: localized('Date'),
		component: (
			<SkioldDatePicker
				onDateChanged={onDateChanged}
				selectedDate={selectedDate}
				theme={'dark'}
				color={'grey'}
				minDate={minDate}
			/>
		),
	};
}

export function getStableSectionPickerRow(
	sectionId: string | undefined,
	penId: string | undefined,
	onSectionSelected: (selection: string) => void,
): FormRow {
	return {
		name: localized('StableSection'),
		component: (
			<SkioldStableSectionPicker
				usedInsideForm={true}
				AnimalType={AnimalType.Sow}
				allowYoungFemale={true}
				onStableSectionSelected={onSectionSelected}
				penId={penId}
				sectionId={sectionId}
				isPigProduction={true}
			/>
		),
	};
}

export function getIdNumberRow(
	entranceType: string,
	idNumberChanged: (idNumber: string) => void,
	idNumberOnblur: () => void,
	openIdNumberModal: () => void,
	idNumber?: string,
): FormRow {
	if (entranceType === 'entranceTypeFromYoungAnimal') {
		return {
			name: localized('idNumber'),
			component: (
				<SkioldTouchableOpacity className="margin-right-eight" onPress={openIdNumberModal}>
					<ViewWeb className="display-flex-direction-row">
						<TextWeb className="padding-right-five-font-size-sixteen">
							{idNumber || localized('ChooseIdNumber')}
						</TextWeb>
						<ViewWeb className="picker-young-animals-view-style">
							<SkioldImage
								width={18}
								height={18}
								imageData={ForwardArrowGrey}
								className="image-picker-view-style"
							/>
						</ViewWeb>
					</ViewWeb>
				</SkioldTouchableOpacity>
			),
		};
	} else {
		return {
			name: localized('idNumber'),
			component: <SkioldFormInput onChangeText={idNumberChanged} text={idNumber} onBlur={idNumberOnblur} />,
		};
	}
}

export function getTransponderRow(onChange: (transponderNumber: number | undefined) => void, number?: number): FormRow {
	return {
		name: localized('transponder'),
		component: <SkioldFormIntegerInput onChangeNumber={onChange} text={number} />,
	};
}

export function getPenPickerRow(
	onPenSelected: (penId: string | undefined) => void,
	sectionId?: string,
	stemAnimal?: IStemAnimal,
): FormRow {
	return {
		name: localized('Pen'),
		component: <SkioldFormPenPicker usedInsideForm={true} onPenSelected={onPenSelected} sectionId={sectionId} />,
	};
}

export function getFeedCurvePickerRow(
	feedCurveNumber: number,
	penId: string | undefined,
	onChange: (number: number | undefined) => void,
	animalKind: AnimalKind | undefined,
): FormRow {
	return {
		name: localized('feedCurve'),
		component: (
			<SkioldFeedCurvePicker
				usedInsideForm={true}
				feedCurveNumber={feedCurveNumber}
				penId={penId}
				onChange={onChange}
				animalKind={animalKind}
			/>
		),
	};
}

export function getAgeInWeeksRow(birthDate: Date | undefined): FormRow {
	return {
		name: localized('age'),
		component: <SkioldFormTextField>{birthDate ? calculateAgeInWeeks(birthDate) : ''}</SkioldFormTextField>,
	};
}

export function getLocationRow(youngAnimal: IStemAnimal | undefined): FormRow {
	return {
		name: localized('currentLocation'),
		component: <SkioldFormTextField>{youngAnimal ? getLocationString(youngAnimal) : ''}</SkioldFormTextField>,
	};
}

export function getDeviationPeriodInputRow(
	deviationPeriodChanged: (number: number | undefined) => void,
	deviationPeriod?: number,
): FormRow {
	return {
		name: localized('DeviationPeriod'),
		component: <SkioldFormIntegerInput onChangeNumber={deviationPeriodChanged} text={deviationPeriod} />,
	};
}
export function getTempFeedDeviationInputRow(
	tempFeedDeviationChanged: (number: number | undefined) => void,
	tempFeedDeviation?: number,
): FormRow {
	return {
		name: localized('TempFeedDeviation'),
		component: <SkioldFormIntegerInput onChangeNumber={tempFeedDeviationChanged} text={tempFeedDeviation} />,
	};
}
export function getFixedDeviationInputRow(
	fixedDeviationChanged: (number: number | undefined) => void,
	fixedDeviation?: number,
): FormRow {
	return {
		name: localized('fixedPercent'),
		component: <SkioldFormIntegerInput onChangeNumber={fixedDeviationChanged} text={fixedDeviation} />,
	};
}
